import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import DramaCard from '@/components/base/DramaCard';
import DetailHero from './components/DetailHero';
import EpisodeList from './components/EpisodeList';
import { useFavorites } from '@/hooks/useFavorites';
import { getDramaById, getRelatedDramas } from '@/utils/drama';

export default function Detail() {
  const { id } = useParams();
  const drama = getDramaById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(''), 2000);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!drama) {
    return (
      <SiteLayout>
        <title>剧集不存在 - 善良的小姨子在线</title>
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-error-warning-line text-3xl"></i>
          </span>
          <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">
            抱歉，没有找到这部剧
          </h1>
          <p className="mt-2 max-w-md text-sm text-foreground-600">
            该剧集可能已被移除，或者链接地址有误。你可以返回分类页浏览其他热门剧集。
          </p>
          <Link
            to="/category"
            className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-compass-3-line"></i>
            浏览全部剧集
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = getRelatedDramas(drama, 4);
  const fav = isFavorite(drama.id);

  const handleToggleFav = () => {
    const next = toggleFavorite(drama.id);
    setToast(next ? '已加入收藏，刷新后仍会保留' : '已取消收藏');
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast('链接已复制到剪贴板');
    } catch {
      setToast('复制失败，请手动复制地址栏链接');
    }
  };

  return (
    <SiteLayout>
      <title>{`${drama.title} - 详情介绍与在线观看 - 善良的小姨子在线`}</title>
      <meta name="description" content={`${drama.title}：${drama.region}${drama.year}年${drama.genres.join('、')}剧，主演${drama.actors.join('、')}。${drama.synopsis.slice(0, 40)}...`} />
      <meta name="keywords" content={`${drama.title},${drama.title}在线观看,${drama.genres.join(',')},善良的小姨子在线`} />

      <div className="mx-auto max-w-6xl px-4 pt-6 md:px-6">
        <nav className="flex flex-wrap items-center gap-1 text-sm text-foreground-600" aria-label="面包屑">
          <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line text-foreground-300"></i>
          <Link to="/category" className="transition-colors hover:text-primary-600">电视剧分类</Link>
          <i className="ri-arrow-right-s-line text-foreground-300"></i>
          <span className="text-foreground-900">{drama.title}</span>
        </nav>
      </div>

      <div className="mt-4">
        <DetailHero drama={drama} isFav={fav} onToggleFav={handleToggleFav} onShare={handleShare} />
      </div>

      <div className="mx-auto max-w-6xl px-4 py-8 md:px-6">
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
                <i className="ri-file-text-line text-primary-500"></i>
                剧情简介
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
                {drama.synopsis}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {drama.genres.map((g) => (
                  <Link
                    key={g}
                    to={`/category?genre=${encodeURIComponent(g)}`}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200 cursor-pointer"
                  >
                    {g}
                  </Link>
                ))}
              </div>
            </section>

            <EpisodeList drama={drama} />
          </div>

          <aside className="lg:col-span-1">
            <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
                <i className="ri-magic-line text-primary-500"></i>
                相关推荐
              </h2>
              <p className="mt-1 text-xs text-foreground-500">根据类型标签为你推荐相似作品</p>
              <div className="mt-4 space-y-3">
                {related.map((d) => (
                  <Link
                    key={d.id}
                    to={`/detail/${d.id}`}
                    className="group flex gap-3 rounded-md p-2 transition-colors hover:bg-primary-50/50 cursor-pointer"
                  >
                    <div className="h-20 w-14 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img
                        src={d.poster}
                        alt={`${d.title} 海报`}
                        title={`${d.title} 相关推荐`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors">
                        {d.title}
                      </h3>
                      <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-600">
                        {d.synopsis}
                      </p>
                      <p className="mt-1 text-xs text-foreground-500">
                        {d.region} · {d.year}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          </aside>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 pb-4 md:px-6">
        <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-semibold text-foreground-950">
          <i className="ri-stack-line text-primary-500"></i>
          你可能还喜欢
        </h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 md:gap-5">
          {related.map((d, i) => (
            <DramaCard key={d.id} drama={d} index={i} />
          ))}
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-md bg-foreground-950 px-4 py-2.5 text-sm text-background-50 animate-soft-in">
          {toast}
        </div>
      )}
    </SiteLayout>
  );
}